import React from 'react';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import GridLoader from '@hobs/components/Grid/GridLoader';
import Columns from '@hobs/components/Grid/Columns';
import WithRoles from '@hobs/hocs/WithRoles';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import { H2 } from '@hobs/components/Headings';
import WithQuickSearch from '@hobs/hocs/WithQuickSearch';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { CellDate, CellDateTime } from '@hobs/components/Grid/CellDate';
import Switch from '@hobs/components/Switch';
import { PAGE_SIZES, USERS_EXPORT_FILE_NAME } from '@hobs/utils/constants';
import { Grid } from '@progress/kendo-react-grid';
import { createLoadingSelector } from '@hobs/utils/loading';
import { queryOData, resetOData, queryXLSOData } from '@hobs/actions/odata';
import { getUserGrid } from '@hobs/actions/gridSelections';
import _Users from '@hobs/containers/Users';
import { parse } from '@hobs/utils/formatWithTimeZone';
import UserDialog from './UserDialog';

class Users extends _Users {
  requestUrl = 'UsersQuery';

  constructor(props) {
    super(props);
    this.state = {
      dataState: {
        take: 5,
        skip: 0,
        sort: [{ field: 'FirstName', dir: 'asc' }]
      }
    };

    this.exportRef = React.createRef();
  }

  _getDefaultColumns() {
    const { t } = this.props;

    return {
      FirstName: {
        title: t('First name'),
        columnMenu: true,
        default: true,
        visible: true
      },
      LastName: {
        title: t('Last name'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Username: {
        title: t('Username'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Email: {
        title: t('Email'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Phone: {
        title: t('Phone'),
        columnMenu: true,
        default: true,
        visible: true
      },
      UnitName: {
        title: t('Assigned to'),
        columnMenu: true,
        default: true,
        visible: true
      },
      RoleName: {
        title: t('Role'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          data: this.getCustomFilter('roles', 'RoleId')
        }
      },
      LoginExpiration: {
        title: t('Login expiration'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDate
      },
      LastLogin: {
        title: t('Last login'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDateTime
      },
      Locked: {
        title: t('Acc. Locked'),
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td>
              <div className="users-view--bool-column">
                <Switch disabled={true} checked={dataItem.Locked} />
                {dataItem.Locked ? t('Yes') : t('No')}
              </div>
            </td>
          );
        }
      },
      controls: {
        title: '',
        headerCell: () => <></>,
        cell: this.controls,
        default: true,
        visible: true,
        width: 90,
        xls: false
      }
    };
  }

  getCustomBooleanFilter(field, trueTitle, falseTitle) {
    const { t } = this.props;

    return [
      {
        name: t('Select all'),
        default: true,
        value: ''
      },
      {
        name: trueTitle,
        value: `<guid>${true}</guid>`,
        field: field,
        default: false
      },
      {
        name: falseTitle,
        value: `<guid>${false}</guid>`,
        field: field,
        default: false
      }
    ];
  }

  getDefaultColumns() {
    return this._getDefaultColumns();
  }

  getDisplayData() {
    if (this.props.oData.data.length > 0) {
      const displayData = [].concat(this.props.oData.data);
      return displayData;
    }
    return [];
  }

  exportUsers = async () => {
    await this.getXlsData();
    this.exportRef.current.save();
  };

  async getXlsData() {
    const { t } = this.props;

    const dataState = this.getDataState();
    delete dataState.take;
    dataState.skip = 0;
    const XLSoData = await this.props.queryXLSOData(this.requestUrl, dataState);
    const data = XLSoData.data.map(item => {
      item.LoginExpiration = parse(item.LoginExpiration);
      item.LastLogin = item.LastLogin ? parse(item.LastLogin) : null;
      item.Locked = item.Locked ? t('Yes') : t('No');
      return item;
    });

    this.setState({
      XLSoData: data
    });
  }

  getDataState() {
    const { withQuickSearchFilter } = this.props;
    const { dataState } = this.state;
    let filters = [];

    if (Object.keys(withQuickSearchFilter).length > 0) {
      filters.push(withQuickSearchFilter);
    }

    const resultDataState = {
      ...dataState
    };

    if (dataState.filter && dataState.filter.filters.length > 0) {
      filters = filters.concat(dataState.filter);
    }

    if (filters.length > 0) {
      resultDataState.filter = {
        logic: 'and',
        filters
      };
    }

    return resultDataState;
  }

  componentDidMount() {
    this.props.getUserGrid();
    this.loadODataQuery();
  }

  getDefaultSnColumns() {
    var defaultColumns = this.getDefaultColumns();

    if (defaultColumns.IsActive) {
      delete defaultColumns.IsActive;
    }

    return defaultColumns;
  }

  getUserPathName(userId = '') {
    return `${this.props.location.pathname.replace(
      '/user',
      ''
    )}/user/${userId}`;
  }

  dataStateChange = e => this.loadUserOdataQuery(e.dataState);

  onQuickSearch = () => this.loadUserOdataQuery(this.state.dataState);

  loadODataQuery = () =>
    this.props.queryOData(this.requestUrl, this.getDataState());

  loadUserOdataQuery(state) {
    this.setState({ dataState: state }, () => {
      this.loadODataQuery();
    });
  }

  render() {
    const columns = this.getDefaultColumns();

    const {
      t,
      location,
      match,
      history,
      partyId,
      units,
      assignedUnits,
      onUserUpdated
    } = this.props;
    return (
      <div className="users-view">
        <UserDialog
          location={location}
          match={match}
          history={history}
          partyId={partyId}
          units={units}
          assignedUnits={assignedUnits}
          reloadGrid={this.loadODataQuery}
          onUserUpdated={onUserUpdated}
        />
        <H2>{t('Manage users')}</H2>
        <div>
          <div
            className="users-view--header"
            style={{ justifyContent: 'space-between' }}
          >
            <div className="users-view--left">
              <div>
                <RaisedButton
                  iconLeft={<Icon icon="plus" />}
                  to={this.getUserPathName()}
                >
                  {t('Add new user')}
                </RaisedButton>
              </div>
              <div>
                {this.props.withQuickSearchForm({
                  onSearch: this.onQuickSearch,
                  noMargin: true,
                  placeholder: 'Search user details'
                })}
              </div>
            </div>
            <div>
              <GhostButton
                disabled={this.getDisplayData().length === 0}
                iconLeft={<Icon icon="download" />}
                onClick={() => {
                  this.exportUsers();
                }}
              >
                {t('XLSX')}
              </GhostButton>
            </div>
          </div>
          <ExcelExport
            ref={this.exportRef}
            data={this.state.XLSoData}
            fileName={USERS_EXPORT_FILE_NAME}
          >
            {Object.keys(columns).reduce((results, key) => {
              const column = columns[key];
              if (
                (typeof column.xls === 'undefined' || column.xls) &&
                column.visible
              ) {
                if (column.filter === 'date') {
                  results.push(
                    <ExcelExportColumn
                      key={ShortId.generate()}
                      width={125}
                      field={column.xlsField || key}
                      title={column.title}
                    />
                  );
                } else {
                  results.push(
                    <ExcelExportColumn
                      key={ShortId.generate()}
                      field={column.xlsField || key}
                      title={column.title}
                    />
                  );
                }
              }
              return results;
            }, [])}
          </ExcelExport>
          <Grid
            sortable={true}
            pageable={{
              pageSizes: PAGE_SIZES
            }}
            {...this.state.dataState}
            {...this.props.oData}
            onDataStateChange={this.dataStateChange}
          >
            {Columns({
              columns: this.getDefaultSnColumns(),
              dataState: this.state.dataState
            })}
          </Grid>
          {this.props.isLoading && <GridLoader />}
        </div>
      </div>
    );
  }
}

Users.propTypes = {
  withQuickSearchForm: PropTypes.func,
  withQuickSearchFilter: PropTypes.object,
  queryXLSOData: PropTypes.func,

  isLoading: PropTypes.bool,
  oData: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  loadODataQuery: PropTypes.func,
  getUserGrid: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  t: PropTypes.func,
  partyId: PropTypes.string,
  units: PropTypes.array,
  assignedUnits: PropTypes.array,
  partyType: PropTypes.number,
  hasUserPermission: PropTypes.func
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state),
  oData: state.odata,
  userGrid: state.gridSelections.userGrid
});

const mapDispatchToProps = {
  queryOData,
  resetOData,
  getUserGrid,
  queryXLSOData
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WithQuickSearch(withTranslation()(WithRoles(Users))));
